import { Box, Grid } from "@mui/material";
import QrCode2RoundedIcon from "@mui/icons-material/QrCode2Rounded";
import { useEffect, useState, forwardRef } from "react";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaCalendarAlt } from "react-icons/fa";
import { IoMdArrowDropdown } from "react-icons/io";
import Icon from "../doctor-console/Utils/Icons";
import { normalizeBase64Image, isTodayOrFuture } from "../utils/utils";
import PaymentModal from "./PaymentModal";
import CaseSheetModal from "./CaseSheetModal";
import PhysiotherapySheetModal from "./physiotherapy/PhysiotherapySheetModal";
import { usePermissions } from "../login/PermissionContext";
import { displayPatientNameFromParts } from "../../utils/displayPatientName";

// Inline panel to display appointment list on the main page

const CustomInput = forwardRef(({ value, onClick }, ref) => (
  <div
    className="custom-date-input"
    onClick={onClick}
    ref={ref}
    style={{
      display: "flex",
      alignItems: "center",
      border: "1px solid #ccc",
      borderRadius: "20px",
      padding: "6px 12px",
      cursor: "pointer",
      gap: "6px",
      width: "fit-content",
      color: "#1c2c4c",
      fontWeight: 500,
      fontSize: "14px",
    }}
  >
    <FaCalendarAlt style={{ fontSize: "14px" }} />
    <span>{value}</span>
    <IoMdArrowDropdown style={{ fontSize: "16px", marginLeft: "4px" }} />
  </div>
));

export default function AppointmentListPanel({
  // controls and data
  appointmentFlag,
  setDateForAppointment,
  dateForAppointment,
  view = "day",
  doctorAppointmentList,
  loadingAppointments,
  patientDataDict,
  selectedDoctorData,
  // handlers
  getTime,
  handleEditButtonClick,
  handleCancelAppointment,
  handleCompletedAppointment,
  handleContextModalOpen,
  handleUploadModalOpen,
  handleVitalModalOpen,
  handleGetPatientData,
  handleConsentModalOpen,
  handleOpenPrescriptionUploadModal,
  handleDataReceived,
  selectPatient,
  showConfirmDialog,
  // variables
  roleTypeUser,
  roleType,
  isAbdmEnabled,
  isAiEnabled,
  FHIRData,
  fhirArrData,
  prescriptionList,
  finalStatus,
  rowSelected,
  currentPatientData,
  isLoading,
  history,
  Cancelled,
  Completed,
  ProgressBar,
  completedAppointments,
  cancelledAppointmentsList,
  pendingAppointments,
  onlineAppointments,
  offlineAppointments,
  onPaymentModalClose,
}) {
  const [appointmentDisplayData, setAppointmentDisplayData] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isUpiModalOpen, setIsUpiModalOpen] = useState(false);
  const [isCaseSheetOpen, setIsCaseSheetOpen] = useState(false);
  const [isPhysiotherapySheetOpen, setIsPhysiotherapySheetOpen] =
    useState(false);

  const { userInfo } = usePermissions();
  const allow_backdate_appointment = userInfo?.allow_backdate_appointment;

  // Hardcoded payment details
  const statusChipCommonSx = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: 28,
    minWidth: 80,
    px: 1.5,
    borderRadius: "999px",
    fontSize: 12,
    fontWeight: 600,
    border: "1px solid #BAE0DD",
    textTransform: "capitalize",
  };

  // Short date formatter (e.g., 19 Nov 2000)
  const formatDobShort = (dobString) => {
    if (!dobString) return undefined;
    const d = new Date(dobString);
    if (isNaN(d.getTime())) return dobString;
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const ActionItem = ({
    title,
    subtitle,
    icon,
    onClick,
    disabled = false,
    bg,
    hoverBg,
    textColor,
  }) => (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        border: "1px solid #E3EFF0",
        borderRadius: "10px",
        backgroundColor: disabled ? "#F5F7FA" : bg || "#FFFFFF",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.7 : 1,
        transition: "background-color 0.2s ease, box-shadow 0.2s ease",
        "&:hover": disabled
          ? {}
          : {
            backgroundColor: hoverBg || "#F7FCFC",
            boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
          },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <span
          style={{
            color:
              (disabled
                ? undefined
                : bg === "#FFFFFF" || (!bg && !disabled)
                  ? undefined
                  : textColor) || "#1c2c4c",
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          {title}
        </span>
        {subtitle && (
          <span
            style={{
              color:
                (disabled
                  ? undefined
                  : bg === "#FFFFFF" || (!bg && !disabled)
                    ? undefined
                    : textColor) || "#6B7A90",
              fontWeight: 400,
              fontSize: 12,
            }}
          >
            {subtitle}
          </span>
        )}
      </Box>
    </Box>
  );

  useEffect(() => {
    switch (appointmentFlag) {
      case "completed":
        setAppointmentDisplayData(completedAppointments);
        break;
      case "pending":
        setAppointmentDisplayData(pendingAppointments);
        break;
      case "canceled":
        setAppointmentDisplayData(cancelledAppointmentsList);
        break;
      case "online":
        setAppointmentDisplayData(onlineAppointments);
        break;
      case "offline":
        setAppointmentDisplayData(offlineAppointments);
        break;
      case "total":
        setAppointmentDisplayData(doctorAppointmentList);
        break;
      default:
        setAppointmentDisplayData([]);
    }
  }, [
    appointmentFlag,
    completedAppointments,
    pendingAppointments,
    cancelledAppointmentsList,
    onlineAppointments,
    offlineAppointments,
    doctorAppointmentList,
  ]);

  // Reset selection if the list changes or becomes shorter
  useEffect(() => {
    if (
      selectedIndex !== null &&
      (selectedIndex < 0 || selectedIndex >= appointmentDisplayData.length)
    ) {
      setSelectedIndex(null);
    }
  }, [appointmentDisplayData, selectedIndex]);

  const handlePaymentStatusUpdated = (
    newPaymentStatus,
    paymentAmount,
    paymentMode
  ) => {
    setAppointmentDisplayData((prev) => {
      if (!prev || !Array.isArray(prev)) return prev;
      const next = [...prev];
      if (selectedIndex !== null && next[selectedIndex]) {
        next[selectedIndex] = {
          ...next[selectedIndex],
          payment_status: newPaymentStatus,
          payment_amount: paymentAmount,
          payment_mode: paymentMode,
        };
      }
      return next;
    });
  };

  const handleUpiModalClose = () => {
    setIsUpiModalOpen(false);
  };

  // console.log("dateForAppointment from appointment list panel", dateForAppointment?.toISOString(), new Date().toISOString());

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Header */}
      {/* <Box
        sx={{
          backgroundColor: "#213360",
          borderRadius: "12px",
          px: 2,
          py: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          className="main-title"
          style={{
            textAlign: "center",
            color: "#ffff",
            margin: 0,
            fontSize: "20px",
          }}
        >
          Today’s Appointments ({appointmentDisplayData.length})
        </div>
      </Box> */}

      {/* Filters Row */}
      <Box sx={{ height: "6vh" }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <DatePicker
              selected={dateForAppointment}
              onChange={(date) => setDateForAppointment(date)}
              dateFormat="dd MMM"
              customInput={<CustomInput />}
              popperProps={{
                strategy: "fixed",
                modifiers: [
                  {
                    name: "zIndex",
                    enabled: true,
                    phase: "write",
                    fn({ state }) {
                      state.styles.popper.zIndex = 99999;
                    },
                  },
                ],
              }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={8}
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
          >
            {/* <Button
              variant="outlined"
              sx={{
                width: { xs: "120px", sm: "140px", md: "160px" },
                height: "40px",
                backgroundColor: "#f0f8ff",
                borderColor: "#21cdc0",
                color: "#21cdc0",
                fontWeight: "bold",
                borderRadius: "10px",
                textTransform: "none",
                "&:hover": { backgroundColor: "#e6f7ff" },
              }}
            >
              New Patient
            </Button>
            <Button
              variant="outlined"
              sx={{
                width: { xs: "120px", sm: "140px", md: "160px" },
                height: "40px",
                backgroundColor: "#f9fff9",
                borderColor: "green",
                color: "green",
                fontWeight: "bold",
                borderRadius: "10px",
                textTransform: "none",
                "&:hover": { backgroundColor: "#eaffea" },
              }}
            >
              Existing Patient
            </Button> */}
          </Grid>
        </Grid>
      </Box>

      {/* List */}
      {view === "day" && (
        <Grid container spacing={2} sx={{ mt: 1, alignItems: "stretch" }}>
          {/* Left column: scrollable list of patient cards */}
          <Grid item xs={12} md={8} sx={{ display: "flex" }}>
            <Box
              sx={{
                flex: 1,
                height: { xs: "30vh", md: "70vh" },
                overflowY: "auto",
                pr: { md: 1 },
                WebkitOverflowScrolling: "touch",
              }}
            >
              {appointmentDisplayData.length > 0 ? (
                appointmentDisplayData.map((e, i) => {
                  const patient =
                    patientDataDict && Object.keys(patientDataDict).length > 0
                      ? patientDataDict[appointmentDisplayData[i].patient]
                      : null;
                  const patientName = patient
                    ? displayPatientNameFromParts(patient)
                    : "";
                  const isScheduled =
                    appointmentDisplayData[i]?.status === "SCHEDULED";
                  const isCancelled =
                    appointmentDisplayData[i]?.status === "CANCELLED";
                  const isCompleted = !isScheduled && !isCancelled;
                  const gender = patient?.gender;
                  const ageVal = patient?.age;
                  const dobVal = patient?.dob
                    ? formatDobShort(patient?.dob)
                    : undefined;
                  const phoneVal = patient?.phone_no || patient?.phone_number;
                  const paymentStatusVal =
                    appointmentDisplayData[i]?.payment_status;
                  const isPaidStatus =
                    (paymentStatusVal || "").toUpperCase() === "PAID";
                  const isRefundCompleteStatus =
                    (paymentStatusVal || "").toUpperCase() === "REFUND";
                  const otherDetails = [
                    gender || null,
                    ageVal !== undefined && ageVal !== null
                      ? `${ageVal}y`
                      : null,
                    dobVal || null,
                    phoneVal || null,
                  ]
                    .filter(Boolean)
                    .join(" • ");
                  return (
                    <Box
                      key={i}
                      onClick={() => setSelectedIndex(i)}
                      sx={{
                        border:
                          selectedIndex === i
                            ? "2px solid #21cdc0"
                            : "1px solid #21cdc0",
                        borderRadius: "10px",
                        backgroundColor: "#EBFAF9",
                        mb: 2,
                        p: 2,
                        display: "flex",
                        gap: 2,
                        alignItems: "center",
                        cursor: "pointer",
                        transition:
                          "background-color 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                        "&:hover": { boxShadow: "0 2px 10px rgba(0,0,0,0.06)" },
                      }}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          overflow: "hidden",
                          flexShrink: 0,
                        }}
                      >
                        <img
                          src={
                            patient &&
                              patient.photo !== "" &&
                              patient.photo !== null
                              ? normalizeBase64Image(patient.photo)
                              : "/img/patient_Appointment.png"
                          }
                          alt="Patient Photo"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <span
                          className="patient-name"
                          style={{
                            fontSize: 16,
                            fontWeight: 700,
                            color: "#1c2c4c",
                            textTransform: 'uppercase',
                          }}
                        >
                          {patientName}
                        </span>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: 0.25,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                          }}
                          title={`${getTime(
                            appointmentDisplayData[i].start_time_ist
                          )} - ${getTime(
                            appointmentDisplayData[i].end_time_ist
                          )}${otherDetails ? " • " + otherDetails : ""}`}
                        >
                          <span
                            className="patient-time-gray"
                            style={{ whiteSpace: "nowrap" }}
                          >
                            {getTime(appointmentDisplayData[i].start_time_ist)}{" "}
                            - {getTime(appointmentDisplayData[i].end_time_ist)}
                          </span>
                          {gender ? (
                            <Box
                              sx={{
                                px: 1,
                                py: 0.25,
                                border: "1px solid #C8D0D9",
                                borderRadius: "999px",
                                fontSize: 11,
                                color: "#50627A",
                                backgroundColor: "#FFFFFF",
                              }}
                            >
                              {gender}
                            </Box>
                          ) : null}
                          {ageVal !== undefined && ageVal !== null ? (
                            <Box
                              sx={{
                                px: 1,
                                py: 0.25,
                                border: "1px solid #C8D0D9",
                                borderRadius: "999px",
                                fontSize: 11,
                                color: "#50627A",
                                backgroundColor: "#FFFFFF",
                              }}
                            >
                              {`${ageVal}y`}
                            </Box>
                          ) : null}
                          {/* {dobVal ? (
                        <Box
                          sx={{
                            px: 1,
                            py: 0.25,
                            border: "1px solid #C8D0D9",
                            borderRadius: "999px",
                            fontSize: 11,
                            color: "#50627A",
                            backgroundColor: "#FFFFFF",
                          }}
                        >
                          {dobVal}
                        </Box>
                      ) : null} */}
                          {phoneVal ? (
                            <Box
                              sx={{
                                px: 1,
                                py: 0.25,
                                border: "1px solid #C8D0D9",
                                borderRadius: "999px",
                                fontSize: 11,
                                color: "#50627A",
                                backgroundColor: "#FFFFFF",
                              }}
                            >
                              {phoneVal}
                            </Box>
                          ) : null}
                        </Box>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Box
                          sx={{
                            ...statusChipCommonSx,
                            color:
                              isPaidStatus || isRefundCompleteStatus
                                ? "#0b3d0b"
                                : "#7A0E0E",
                            backgroundColor:
                              isPaidStatus || isRefundCompleteStatus
                                ? "#A1DA3B"
                                : "#F9C0C0",
                          }}
                          // title={`Payment status: ${
                          //   isPaidStatus ? "Paid" : "Unpaid"
                          // }`}
                          title={`Payment status: ${paymentStatusVal === "PAID"
                            ? "Paid"
                            : paymentStatusVal === "INITIATE REFUND"
                              ? "Refund Initiated"
                              : paymentStatusVal === "REFUND"
                                ? "Refund Completed"
                                : "Unpaid"
                            }`}
                        >
                          {/* {isPaidStatus ? "paid" : "unpaid"} */}
                          {paymentStatusVal === "PAID"
                            ? "Paid"
                            : paymentStatusVal === "INITIATE REFUND"
                              ? "Refund Initiated"
                              : paymentStatusVal === "REFUND"
                                ? "Refund Completed"
                                : "Unpaid"}
                        </Box>
                        <Box
                          sx={{
                            ...statusChipCommonSx,
                            color: isCancelled
                              ? "#5D3A00"
                              : isCompleted
                                ? "#0b3d0b"
                                : "#0E3F66",
                            backgroundColor: isCancelled
                              ? "#FFBD8A"
                              : isCompleted
                                ? "#A1DA3B"
                                : "#E9FAF9",
                          }}
                        >
                          {isCancelled
                            ? "cancelled"
                            : isCompleted
                              ? "completed"
                              : "scheduled"}
                        </Box>
                      </Box>
                    </Box>
                  );
                })
              ) : loadingAppointments ? (
                <Grid item xs={12}>
                  <img
                    style={{ width: "30%", marginTop: "10px" }}
                    src={ProgressBar}
                    alt="Loading..."
                  />
                </Grid>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    minHeight: "42vh",
                    color: "#6B7A90",
                    border: "1px dashed #C8D0D9",
                    borderRadius: "10px",
                    p: 4,
                  }}
                >
                  <Box
                    sx={{
                      mb: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon name="noappointment" size={72} />
                  </Box>
                  <div style={{ fontSize: 16, fontWeight: 500 }}>
                    No appointments to show
                  </div>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Right column: common actions panel */}
          <Grid item xs={12} md={4} sx={{ display: "flex" }}>
            <Box
              sx={{
                p: 2,
                border: "1px solid #E3EFF0",
                borderRadius: "12px",
                backgroundColor: "#FFFFFF",
                width: "100%",
                height: { xs: "auto", md: "70vh" },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  mb: 1.5,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span style={{ color: "#1c2c4c", fontWeight: 700 }}>
                  Actions
                </span>
                {/* s */}
              </Box>

              {selectedIndex === null ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    flex: 1,
                    color: "#6B7A90",
                  }}
                >
                  <div style={{ fontSize: 16, fontWeight: 500 }}>
                    Select a patient to view available actions
                  </div>
                </Box>
              ) : (
                (() => {
                  const appt = appointmentDisplayData[selectedIndex];
                  if (!appt) {
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "column",
                          flex: 1,
                          color: "#6B7A90",
                        }}
                      >
                        <div style={{ fontSize: 16, fontWeight: 500 }}>
                          Selection is no longer available. Please pick another
                          patient.
                        </div>
                      </Box>
                    );
                  }
                  const isScheduled = appt?.status === "SCHEDULED";
                  const isCancelled = appt?.status === "CANCELLED";
                  const isNotPaid =
                    appt?.payment_status === null ||
                    appt?.payment_status === "UNPAID";
                  const isRefundCompleted = appt?.payment_status === "REFUND";
                  //Based on allow_backdate_appointment, Allow actions for today and future dates; restrict only for past dates
                  const isSelectedDateTodayOrFuture =
                    isTodayOrFuture(dateForAppointment);
                  const restrictActions =
                    isScheduled
                    && !isSelectedDateTodayOrFuture && !allow_backdate_appointment;
                  const patient =
                    appt &&
                      patientDataDict &&
                      Object.keys(patientDataDict).length > 0
                      ? patientDataDict[appt.patient]
                      : null;
                  const department = String(
                    selectedDoctorData?.department ??
                    ""
                  ).toLowerCase();
                  console.log("departmentId", department);
                  console.log("selectedDoctorData", selectedDoctorData);
                  const canAccessPhysiotherapySheet =
                    department.includes("physiotherapy");
                  return (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          flex: 1,
                          overflowY: "auto",
                          pr: 0.5,
                        }}
                      >
                        {canAccessPhysiotherapySheet && <ActionItem
                          title="Physiotherapy Sheet"
                          subtitle="Open physiotherapy sheet"
                          icon={<DescriptionOutlinedIcon sx={{ color: "#0E3F66" }} />}
                          onClick={() => setIsPhysiotherapySheetOpen(true)}
                          // disabled={
                          //   !canAccessPhysiotherapySheet || !isScheduled || restrictActions
                          // }
                          bg="#FFFFFF"
                          hoverBg="#F7FCFC"
                        />}
                        <ActionItem
                          title="Start Consultation"
                          subtitle="Go to Doctor Console"
                          icon={
                            <Icon
                              name={
                                roleType !== "doctor"
                                  ? "disabledgotodrconsole"
                                  : "start"
                              }
                              size={24}
                            />
                          }
                          onClick={async () => {
                            if (!isAbdmEnabled) {
                              history.push({
                                pathname: `/doctor/DoctorConsole`,
                                state: {
                                  patientData: {
                                    ...patientDataDict[
                                    appointmentDisplayData[selectedIndex]
                                      .patient
                                    ],
                                    appointment_type:
                                      appointmentDisplayData[selectedIndex]
                                        .appointment_type,
                                  },
                                  reports: FHIRData,
                                  fhirArrData: fhirArrData,
                                  prescriptionList: prescriptionList,
                                  doctorData: selectedDoctorData,
                                  appointmentId:
                                    appointmentDisplayData[selectedIndex]
                                      .appointment_id,
                                  dateForAppointment: dateForAppointment,
                                  startTime: appointmentDisplayData[selectedIndex].start_time,
                                },
                              });
                            } else if (
                              finalStatus === "Data Received from HIP" &&
                              rowSelected === selectedIndex
                            ) {
                              handleDataReceived(selectedIndex);
                            } else if (
                              rowSelected === "" &&
                              currentPatientData.length > 0 &&
                              fhirArrData.length === 0 &&
                              !isLoading
                            ) {
                              const message =
                                "Do you want to proceed with the available health records?";
                              const confirmed = await showConfirmDialog(
                                message
                              );
                              if (confirmed) {
                                selectPatient(
                                  appointmentDisplayData[selectedIndex].patient,
                                  selectedIndex
                                );
                              }
                            } else {
                              const message =
                                "Do you want to proceed with the available health records?";
                              const confirmed = await showConfirmDialog(
                                message
                              );
                              if (confirmed) {
                                selectPatient(
                                  appointmentDisplayData[selectedIndex].patient,
                                  selectedIndex
                                );
                              } else {
                                history.push({
                                  pathname: `/doctor/DoctorConsole`,
                                  state: {
                                    patientData: {
                                      ...patientDataDict[
                                      appointmentDisplayData[selectedIndex]
                                        .patient
                                      ],
                                      appointment_type:
                                        appointmentDisplayData[selectedIndex]
                                          .appointment_type,
                                    },
                                    reports: FHIRData,
                                    fhirArrData: [],
                                    prescriptionList: prescriptionList,
                                    doctorData: selectedDoctorData,
                                    appointmentId:
                                      appointmentDisplayData[selectedIndex]
                                        .appointment_id,
                                    dateForAppointment: dateForAppointment,
                                    startTime: appointmentDisplayData[selectedIndex].start_time,
                                  },
                                });
                              }
                            }
                          }}
                          disabled={
                            !isScheduled ||
                            roleType !== "doctor"
                            || canAccessPhysiotherapySheet
                            || restrictActions
                          }
                          bg={roleType === "doctor" ? "#0E3F66" : undefined}
                          hoverBg={
                            roleType === "doctor" ? "#0E3F66" : undefined
                          }
                          textColor={
                            isCancelled
                              ? undefined
                              : roleType === "doctor"
                                ? "#FFFFFF"
                                : undefined
                          }
                        />

                        <ActionItem
                          title="Quick Consultation"
                          subtitle="Handwritten prescription upload"
                          icon={<Icon name="quick" size={24} />}
                          onClick={() =>
                            handleOpenPrescriptionUploadModal(selectedIndex)
                          }
                          disabled={
                            !isScheduled || !isAiEnabled
                            || canAccessPhysiotherapySheet
                            || restrictActions
                          }
                          bg={roleType === "doctor" ? "#176BAD" : undefined}
                          hoverBg={
                            roleType === "doctor" ? "#176BAD" : undefined
                          }
                          textColor={
                            isCancelled
                              ? undefined
                              : roleType === "doctor"
                                ? "#FFFFFF"
                                : undefined
                          }
                        />
                        <ActionItem
                          title="Telemedicine"
                          subtitle="Start video consultation"
                          icon={<Icon name="tele" size={24} />}
                          onClick={async () => { }}
                          // disabled={!isScheduled || !isAiEnabled}
                          disabled={
                            roleType !== "doctor"
                              ? true
                              : !isScheduled
                              || canAccessPhysiotherapySheet
                              || restrictActions
                          }
                          bg={roleType === "doctor" ? "#99CFF9" : undefined}
                          hoverBg={
                            roleType === "doctor" ? "#99CFF9" : undefined
                          }
                          textColor={
                            isCancelled
                              ? undefined
                              : roleType === "doctor"
                                ? "#FFFFFF"
                                : undefined
                          }
                        />
                        <ActionItem
                          title="Case Sheet/Diabetes Profile"
                          subtitle="Open and update case sheet"
                          icon={<DescriptionOutlinedIcon sx={{ color: "#0E3F66" }} />}
                          onClick={() => setIsCaseSheetOpen(true)}
                          disabled={!isScheduled || restrictActions}
                          bg="#FFFFFF"
                          hoverBg="#F7FCFC"
                        />
                        <ActionItem
                          title="Process Payment"
                          subtitle="Payment processing"
                          icon={
                            <QrCode2RoundedIcon sx={{ color: "#213360" }} />
                          }
                          onClick={() => setIsUpiModalOpen(true)}
                          disabled={
                            isCancelled && (isNotPaid || isRefundCompleted)
                            //  ||
                            // (isCancelled && isRefundCompleted)
                          }
                          bg="#FFFFFF"
                          hoverBg="#FFFFFF"
                        />
                        <ActionItem
                          title="Visit Purpose"
                          subtitle="Capture patient's visit purpose"
                          icon={<Icon name="careContext" size={24} />}
                          onClick={() =>
                            handleContextModalOpen(appt.abha_id, appt.patient)
                          }
                          disabled={!isScheduled
                            || restrictActions
                          }
                        />
                        <ActionItem
                          title="Upload Records"
                          subtitle="Upload historical health records"
                          icon={<Icon name="uploadReport" size={24} />}
                          onClick={() => handleUploadModalOpen(appt.abha_id, appt.patient)}
                          disabled={!isScheduled
                            || restrictActions
                          }
                        />
                        {roleTypeUser === "user" ||
                          roleTypeUser === "facility user" ? (
                          <ActionItem
                            title="Add Vitals"
                            subtitle="Record vital signs"
                            icon={<Icon name="vitals" size={24} />}
                            onClick={() =>
                              handleVitalModalOpen(
                                appt.appointment_id,
                                appt.patient
                              )
                            }
                            disabled={!isScheduled
                              || restrictActions
                            }
                          />
                        ) : null}
                        <ActionItem
                          title="ABDM Data"
                          subtitle="Access patient data from ABDM"
                          icon={<Icon name="consentHistory" size={24} />}
                          onClick={() =>
                            handleGetPatientData(patient, selectedIndex)
                          }
                          disabled={!isScheduled}
                        />
                        <ActionItem
                          title="Consent History"
                          subtitle="View patient's consent history"
                          icon={<Icon name="consentHistory" size={24} />}
                          onClick={() => handleConsentModalOpen(patient)}
                          disabled={false}
                        />
                        <Box sx={{ my: 1, borderTop: "1px solid #21cdc0" }} />

                        <ActionItem
                          title="Complete"
                          subtitle="Mark as completed"
                          icon={<Icon name="complete" size={24} />}
                          onClick={() =>
                            handleCompletedAppointment(appt.appointment_id)
                          }
                          disabled={!isScheduled
                            || restrictActions
                          }
                          bg={roleType === "doctor" ? "#FFFFFF" : "#0E3F66"}
                          hoverBg={
                            roleType === "doctor" ? "#FFFFFF" : "#0E3F66"
                          }
                          textColor={
                            isCancelled
                              ? undefined
                              : roleType === "doctor"
                                ? undefined
                                : "#FFFFFF"
                          }
                        />
                        <ActionItem
                          title="Edit Appointment"
                          subtitle="Modify appointment details"
                          icon={<Icon name="edit" size={24} />}
                          onClick={() => handleEditButtonClick(appt)}
                          disabled={!isScheduled
                            || restrictActions
                          }
                          bg={roleType === "doctor" ? "#FFFFFF" : "#176BAD"}
                          hoverBg={
                            roleType === "doctor" ? "#FFFFFF" : "#176BAD"
                          }
                          textColor={
                            isCancelled
                              ? undefined
                              : roleType === "doctor"
                                ? undefined
                                : "#FFFFFF"
                          }
                        />

                        <ActionItem
                          title="Cancel"
                          subtitle="Cancel this appointment"
                          icon={<Icon name="Cancel" size={24} />}
                          onClick={() =>
                            handleCancelAppointment(appt.appointment_id)
                          }
                          disabled={!isScheduled
                            || restrictActions
                          }
                          bg={roleType === "doctor" ? "#FFFFFF" : "#99CFF9"}
                          hoverBg={
                            roleType === "doctor" ? "#FFFFFF" : "#99CFF9"
                          }
                          textColor={
                            isCancelled
                              ? undefined
                              : roleType === "doctor"
                                ? undefined
                                : "#FFFFFF"
                          }
                        />
                      </Box>
                    </>
                  );
                })()
              )}
            </Box>
          </Grid>
        </Grid>
      )}
      <PaymentModal
        open={isUpiModalOpen}
        onClose={handleUpiModalClose}
        onAfterClose={onPaymentModalClose}
        appointment={
          selectedIndex !== null ? appointmentDisplayData[selectedIndex] : null
        }
        selectedDoctorData={selectedDoctorData}
        onStatusUpdated={handlePaymentStatusUpdated}
      />
      <CaseSheetModal
        open={isCaseSheetOpen}
        onClose={() => setIsCaseSheetOpen(false)}
        appointment={
          selectedIndex !== null ? appointmentDisplayData[selectedIndex] : null
        }
        patientData={
          selectedIndex !== null &&
            appointmentDisplayData[selectedIndex] &&
            patientDataDict &&
            Object.keys(patientDataDict).length > 0
            ? patientDataDict[appointmentDisplayData[selectedIndex].patient]
            : null
        }
      />
      <PhysiotherapySheetModal
        open={isPhysiotherapySheetOpen}
        onClose={() => setIsPhysiotherapySheetOpen(false)}
        appointment={
          selectedIndex !== null ? appointmentDisplayData[selectedIndex] : null
        }
        patientData={
          selectedIndex !== null &&
            appointmentDisplayData[selectedIndex] &&
            patientDataDict &&
            Object.keys(patientDataDict).length > 0
            ? patientDataDict[appointmentDisplayData[selectedIndex].patient]
            : null
        }
        selectedDoctorData={selectedDoctorData}
        dateForAppointment={dateForAppointment}
        isAbdmEnabled={isAbdmEnabled}
        onCompleteAppointment={handleCompletedAppointment}
      />
    </Box>
  );
}
